/**
 * Standard AutoCAD linetype registry.
 *
 * Ships the full set of linetypes defined in AutoCAD's stock
 * `acad.lin` / `acadiso.lin` shape files, normalised to nominal
 * millimetre dash/gap pairs suitable for SVG `stroke-dasharray`.
 *
 * These linetypes are ALWAYS available in every drawing — in AutoCAD
 * they are shipped bundled with the application, independent of the
 * DXF file. They do not need to be present in the DXF LTYPE table to
 * be usable (AutoCAD will load them on demand from the .lin file).
 *
 * The three sentinel names — `Continuous`, `ByLayer`, `ByBlock` — are
 * also included so they can appear uniformly in property dropdowns
 * and so callers can round-trip them through the same helpers.
 *
 * See the ТЗ priority #1 — NO simplifications, AutoCAD parity — and
 * CLAUDE.md for the rules this module adheres to.
 *
 * TODO(F14): the canonical NAME-keyed linetype seed now lives in the
 * `drawing-model` core (`packages/drawing-model/src/linetypeSeed.ts`,
 * `STANDARD_LINETYPES: Record<string, Linetype>`). PR-LT3's scene render path
 * resolves patterns from THAT core seed via `linetypeResolve`. This web table
 * is RETAINED for the SVG / PDF / PropertyGrid consumers because it differs in
 * unit (mm here vs inch in core), name casing (`Dashed` here vs `DASHED` in
 * core), and the hand-tuned `…2` half-variant magnitudes — mechanically
 * deriving it from the core seed would change `linetypeToSvgDashArray` output
 * and ~15 assertions in `standardLinetypes.test.ts`. Full dedup (a shared
 * mm↔inch converter feeding both shapes) is deferred to a focused F14 PR.
 */

export type StandardLinetype = {
  name: string;
  description: string;
  /**
   * Dash/gap pattern in millimetre-equivalents. Empty array =
   * continuous (no dashing).
   *
   * Values < 0.1 are treated as dots by {@link linetypeToSvgDashArray}
   * (they round to `0` in the emitted CSS string so the renderer
   * draws a round stroke-cap dot at that position).
   */
  dashPattern: number[];
};

/**
 * The complete set of AutoCAD standard linetypes (both `acad.lin` and
 * `acadiso.lin`). Order matches the order shown in AutoCAD's Linetype
 * Manager so dropdown rendering is stable and predictable.
 *
 * To add a new standard linetype, append it at the end of this array
 * and add a unit test asserting its dash pattern. Do NOT reorder — the
 * Continuous / ByLayer / ByBlock sentinels must come first so the
 * dropdown shows them at the top (matching AutoCAD Properties palette).
 */
export const STANDARD_LINETYPES: StandardLinetype[] = [
  { name: "Continuous", description: "Solid line", dashPattern: [] },
  { name: "ByLayer", description: "Inherit from layer", dashPattern: [] },
  { name: "ByBlock", description: "Inherit from block", dashPattern: [] },
  { name: "Dashed", description: "Dashed _ _ _", dashPattern: [12.5, 6.25] },
  { name: "Dashed2", description: "Dashed (half)", dashPattern: [6.25, 3.125] },
  { name: "DashedX2", description: "Dashed (2x)", dashPattern: [25, 12.5] },
  {
    name: "Center",
    description: "Center ____ _ ____",
    dashPattern: [31.75, 6.35, 6.35, 6.35],
  },
  {
    name: "Center2",
    description: "Center (half)",
    dashPattern: [19.05, 3.175, 3.175, 3.175],
  },
  {
    name: "CenterX2",
    description: "Center (2x)",
    dashPattern: [63.5, 12.7, 12.7, 12.7],
  },
  {
    name: "Dashdot",
    description: "Dash-dot _ . _ . _",
    dashPattern: [12.7, 6.35, 0.01, 6.35],
  },
  {
    name: "Dashdot2",
    description: "Dash-dot (half)",
    dashPattern: [6.35, 3.175, 0.01, 3.175],
  },
  {
    name: "DashdotX2",
    description: "Dash-dot (2x)",
    dashPattern: [25.4, 12.7, 0.01, 12.7],
  },
  { name: "Dot", description: "Dotted . . . .", dashPattern: [0.01, 6.35] },
  { name: "Dot2", description: "Dotted (half)", dashPattern: [0.01, 3.175] },
  { name: "DotX2", description: "Dotted (2x)", dashPattern: [0.01, 12.7] },
  { name: "Hidden", description: "Hidden _ _ _", dashPattern: [6.35, 3.175] },
  {
    name: "Hidden2",
    description: "Hidden (half)",
    dashPattern: [3.175, 1.5875],
  },
  { name: "HiddenX2", description: "Hidden (2x)", dashPattern: [12.7, 6.35] },
  {
    name: "Phantom",
    description: "Phantom ____ _ _ ____",
    dashPattern: [31.75, 6.35, 6.35, 6.35, 6.35, 6.35],
  },
  {
    name: "Phantom2",
    description: "Phantom (half)",
    dashPattern: [19.05, 3.175, 3.175, 3.175, 3.175, 3.175],
  },
  {
    name: "PhantomX2",
    description: "Phantom (2x)",
    dashPattern: [63.5, 12.7, 12.7, 12.7, 12.7, 12.7],
  },
  {
    name: "Divide",
    description: "Divide _ . . _ . .",
    dashPattern: [12.7, 6.35, 0.01, 6.35, 0.01, 6.35],
  },
  {
    name: "Divide2",
    description: "Divide (half)",
    dashPattern: [6.35, 3.175, 0.01, 3.175, 0.01, 3.175],
  },
  {
    name: "DivideX2",
    description: "Divide (2x)",
    dashPattern: [25.4, 12.7, 0.01, 12.7, 0.01, 12.7],
  },
  {
    name: "Border",
    description: "Border _ _ .",
    dashPattern: [12.7, 6.35, 12.7, 6.35, 0.01, 6.35],
  },
  {
    name: "Border2",
    description: "Border (half)",
    dashPattern: [6.35, 3.175, 6.35, 3.175, 0.01, 3.175],
  },
  {
    name: "BorderX2",
    description: "Border (2x)",
    dashPattern: [25.4, 12.7, 25.4, 12.7, 0.01, 12.7],
  },
  {
    name: "ACAD_ISO02W100",
    description: "ISO Dashed",
    dashPattern: [12, 3],
  },
  {
    name: "ACAD_ISO03W100",
    description: "ISO Dashed space",
    dashPattern: [12, 18],
  },
  {
    name: "ACAD_ISO04W100",
    description: "ISO Long-dashed dotted",
    dashPattern: [24, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO05W100",
    description: "ISO Long-dashed double-dotted",
    dashPattern: [24, 3, 0.01, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO06W100",
    description: "ISO Long-dashed triple-dotted",
    dashPattern: [24, 3, 0.01, 3, 0.01, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO07W100",
    description: "ISO Dotted",
    dashPattern: [0.01, 3],
  },
  {
    name: "ACAD_ISO08W100",
    description: "ISO Long-dashed short-dashed",
    dashPattern: [24, 3, 6, 3],
  },
  {
    name: "ACAD_ISO09W100",
    description: "ISO Long-dashed double-short-dashed",
    dashPattern: [24, 3, 6, 3, 6, 3],
  },
  {
    name: "ACAD_ISO10W100",
    description: "ISO Dashed dotted",
    dashPattern: [12, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO11W100",
    description: "ISO Double-dashed dotted",
    dashPattern: [12, 3, 12, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO12W100",
    description: "ISO Dashed double-dotted",
    dashPattern: [12, 3, 0.01, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO13W100",
    description: "ISO Double-dashed double-dotted",
    dashPattern: [12, 3, 12, 3, 0.01, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO14W100",
    description: "ISO Dashed triple-dotted",
    dashPattern: [12, 3, 0.01, 3, 0.01, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO15W100",
    description: "ISO Double-dashed triple-dotted",
    dashPattern: [12, 3, 12, 3, 0.01, 3, 0.01, 3, 0.01, 3],
  },
];

/** Canonical case-folded key for lookups. Whitespace-tolerant. */
function normalize(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Case-insensitive lookup in the standard linetype registry.
 * Returns `undefined` when the name is unknown (caller should treat
 * unknown names as solid, matching AutoCAD's fallback behaviour).
 */
export function findStandardLinetype(
  name: string,
): StandardLinetype | undefined {
  const key = normalize(name);
  if (!key) return undefined;
  return STANDARD_LINETYPES.find((lt) => normalize(lt.name) === key);
}

/**
 * Convert a linetype name to an SVG `stroke-dasharray` attribute
 * string. Returns an empty string for continuous / unknown / sentinel
 * (ByLayer, ByBlock) linetypes so the caller can skip setting the
 * CSS property entirely (solid stroke is the default).
 *
 * Dot markers (dash length < 0.1 mm) are emitted as `0`, which — when
 * combined with `stroke-linecap: round` on the SVG — renders as a
 * round dot centred on the path. This matches how AutoCAD renders
 * the dot segments in dash-dot / divide / border linetypes.
 */
export function linetypeToSvgDashArray(name: string): string {
  const lt = findStandardLinetype(name);
  if (!lt || lt.dashPattern.length === 0) return "";
  return lt.dashPattern.map((v) => (v < 0.1 ? "0" : String(v))).join(",");
}

/**
 * Merge the standard registry into an existing array of entries
 * (typed only as `{ name }` so the helper can seed documents whose
 * own linetype record carries additional fields).
 *
 * Case-insensitive by name: if `existing` already contains e.g. a
 * user-defined `"DASHED"` entry, it is kept and the standard `Dashed`
 * entry is NOT added again. All other standard names are appended so
 * the full AutoCAD library is available regardless of what the DXF
 * file provided.
 *
 * Intended for use on fresh-document creation and on DXF-import
 * merge. Returns a new array — does not mutate `existing`.
 */
export function seedStandardLinetypes<T extends { name: string }>(
  existing: readonly T[],
): (T | StandardLinetype)[] {
  const existingNames = new Set(existing.map((e) => normalize(e.name)));
  return [
    ...existing,
    ...STANDARD_LINETYPES.filter((lt) => !existingNames.has(normalize(lt.name))),
  ];
}
